import { useEffect, useMemo, useState } from 'react';

import { utils } from '@atlaskit/util-service-support/constants';
import type { RequestServiceOptions } from '@atlaskit/util-service-support/types';

type Maybe<T> = T | null;

type RESTRequest<Variables> = {
	url: string;
	body: Variables;
};

type RESTResponse<Data> = Maybe<Data>;

export type ObjectResolverParamType = {
	resourceUrl: string;
};

export type ObjectResolverDataType = {
	body: Body;
	status: number;
	error?: {
		type: string;
		message: string;
		status: number;
	};
};

export type Body = {
	meta: Meta;
	data: ObjectData;
};

export type ObjectData = {
	'@context': Context;
	generator: Generator;
	'@type': string[];
	url: string;
	name: string;
	'atlassian:state': string;
	summary: string;
	updated: Date;
	'schema:dateCreated': Date;
	attributedTo: AtlassianOwnedBy;
	'schema:commentCount': number;
	'atlassian:reactCount': number;
	'atlassian:titlePrefix': AtlassianTitlePrefix;
	'atlassian:ownedBy': AtlassianOwnedBy;
	'atlassian:ari': string;
	preview: Preview;
};

export type Context = {
	'@vocab': string;
	atlassian: string;
	schema: string;
};

export type AtlassianOwnedBy = {
	'@type': string;
	name: string;
	icon: string;
};

export type AtlassianTitlePrefix = {
	text: string;
	'@type': string;
};

export type Generator = {
	'@type': string;
	'@id': string;
	name: string;
	icon: Icon;
};

export type Icon = {
	'@type': string;
	url: string;
};

export type Preview = {
	'@type': string;
	href: string;
	'atlassian:supportedPlatforms': string[];
};

export type Meta = {
	auth: any[];
	definitionId: string;
	product: string;
	visibility: string;
	access: string;
	resourceType: string;
	objectId: string;
	tenantId: string;
	activationId: string;
	containerId: string;
	category: string;
	supportedFeature: string[];
	key: string;
};

interface useFetchSmartLinkDataArgs {
	urls: string[];
}

async function fetchRestResponse<Variables, Data>({ url, body }: RESTRequest<Variables>) {
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		},
	};
	return utils.requestService<RESTResponse<Data>>({ url }, options);
}

const OBJECT_RESOLVER_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';

export const useFetchSmartLinkData: ({ urls }: useFetchSmartLinkDataArgs) => {
	isLoading: boolean;
	objectResolverData: ObjectData[];
} = ({ urls }: useFetchSmartLinkDataArgs) => {
	const [isLoading, setIsLoading] = useState(true);
	const [objectResolverData, setObjectResolverData] = useState<ObjectData[]>([]);
	const urlKeys = useMemo(() => urls.join('|'), [urls]);

	useEffect(() => {
		const fetchObjectResolver = async () => {
			if (!urls.length) {
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				const objectResolver = await fetchRestResponse<
					ObjectResolverParamType[],
					ObjectResolverDataType[]
				>({
					url: OBJECT_RESOLVER_ENDPOINT,
					body: urls.map((url) => ({ resourceUrl: url })),
				});

				if (!objectResolver) {
					setIsLoading(false);
					return;
				}

				const objectData = objectResolver.filter((obj) => obj.status === 200);
				setObjectResolverData(objectData.map((objData) => objData.body.data));
				setIsLoading(false);
			} catch (err) {
				setIsLoading(false);
			}
		};

		void fetchObjectResolver();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [urlKeys]);

	return {
		isLoading,
		objectResolverData,
	};
};
